import EditeurRiche from './EditeurRiche';
import '../assets/styles/ArticlesEditeur.css';

/**
 * Liste d'articles éditable : numéro, titre et corps enrichi pour chacun,
 * avec réordonnancement.
 *
 * Partagé par le paramétrage des modèles et par le panneau du contrat :
 * c'est le même geste — écrire le texte d'un contrat — qu'on le fasse une
 * fois pour toutes dans un modèle ou pour un seul contrat.
 */

const ROMAINS = [
  'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
  'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX',
];

/** 1 -> « I », 14 -> « XIV » ; au-delà de XX, le chiffre arabe. */
export const romain = (n) => ROMAINS[n - 1] || String(n);

export const articleVide = (position) => ({ numero: romain(position), titre: '', corps: '' });

/**
 * @param {object}   p
 * @param {Array}    p.articles  [{ numero, titre, corps }]
 * @param {Function} p.onChange  Reçoit la liste entière à chaque changement.
 * @param {boolean}  [p.disabled]
 * @param {Array}    [p.balises] Vocabulaire {{...}} affiché en aide ; omis dans le panneau.
 */
function ArticlesEditeur({ articles, onChange, disabled = false, balises = null }) {
  const maj = (i, champ, valeur) =>
    onChange(articles.map((a, j) => (j === i ? { ...a, [champ]: valeur } : a)));

  const deplacer = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= articles.length) return;
    const copie = [...articles];
    [copie[i], copie[j]] = [copie[j], copie[i]];
    onChange(copie);
  };

  const retirer = (i) => onChange(articles.filter((_, j) => j !== i));
  const ajouter = () => onChange([...articles, articleVide(articles.length + 1)]);

  return (
    <div className="articles-editeur">
      {balises && balises.length > 0 && (
        <details className="articles-balises">
          <summary>Balises disponibles</summary>
          <ul>
            {balises.map((b) => (
              <li key={b.cle}>
                <code>{`{{${b.cle}}}`}</code> — {b.aide}
              </li>
            ))}
          </ul>
          <p className="articles-balises-note">
            Un passage écrit entre <code>{'{{#cle}}'}</code> et <code>{'{{/cle}}'}</code> ne
            s&apos;imprime que si la balise a une valeur.
          </p>
        </details>
      )}

      {articles.length === 0 && <div className="articles-vide">Aucun article.</div>}

      {articles.map((a, i) => (
        <div className="articles-item" key={i}>
          <div className="articles-item-entete">
            <input
              type="text"
              className="param-input articles-numero"
              value={a.numero}
              onChange={(e) => maj(i, 'numero', e.target.value)}
              maxLength={12}
              placeholder="I"
              aria-label={`Numéro de l'article ${i + 1}`}
              disabled={disabled}
            />
            <input
              type="text"
              className="param-input articles-titre"
              value={a.titre}
              onChange={(e) => maj(i, 'titre', e.target.value)}
              maxLength={200}
              placeholder="Titre de l'article"
              aria-label={`Titre de l'article ${i + 1}`}
              disabled={disabled}
            />
            <div className="articles-item-actions">
              <button
                type="button"
                className="param-btn param-btn-sm"
                onClick={() => deplacer(i, -1)}
                disabled={disabled || i === 0}
                aria-label={`Monter l'article ${i + 1}`}
                title="Monter"
              >
                ↑
              </button>
              <button
                type="button"
                className="param-btn param-btn-sm"
                onClick={() => deplacer(i, 1)}
                disabled={disabled || i === articles.length - 1}
                aria-label={`Descendre l'article ${i + 1}`}
                title="Descendre"
              >
                ↓
              </button>
              <button
                type="button"
                className="param-btn param-btn-sm param-btn-danger"
                onClick={() => retirer(i)}
                disabled={disabled}
                aria-label={`Retirer l'article ${i + 1}`}
                title="Retirer"
              >
                ×
              </button>
            </div>
          </div>
          <EditeurRiche
            value={a.corps}
            onChange={(html) => maj(i, 'corps', html)}
            disabled={disabled}
            minHeight={110}
            placeholder="Corps de l'article…"
          />
        </div>
      ))}

      <button type="button" className="param-btn param-btn-sm" onClick={ajouter} disabled={disabled}>
        + Ajouter un article
      </button>
    </div>
  );
}

export default ArticlesEditeur;
